
import { formatFullName } from '@/lib/format';
import { getPrayerCategories } from '@/lib/categories';
import { Badge, Button, Card, CardFooter, getCategoryBadgeVariant } from '@/components/ui';
import type { QueueItem } from '@/hooks/useProducerQueue';
import type { PrayerRequest } from '@/types';

export interface ShuffleUpNextCardProps {
  item: QueueItem | null;
  queueType: 'prayer' | 'praise';
  /** Optional label for combined mix (e.g. Prayer Request / Answered Prayer) */
  typeLabel?: string;
  position: number;
  onDisplay: (prayerRequestId: string) => void;
  onEdit?: (prayer: PrayerRequest) => void;
  onRemove?: (prayerId: string) => void;
}

export function ShuffleUpNextCard({
  item,
  queueType: _queueType,
  typeLabel,
  position,
  onDisplay,
  onEdit,
  onRemove,
}: ShuffleUpNextCardProps) {
  if (!item) {
    return (
      <Card
        padding="md"
        className="relative border-2 border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/30 text-center py-8"
      >
        <p className="text-sm text-slate-500 dark:text-slate-400">No card queued</p>
      </Card>
    );
  }

  const { prayerRequest, prayerRequestId, displayCount = 0 } = item;
  if (!prayerRequest) {
    return (
      <Card padding="md" className="border-dashed border-amber-300 dark:border-amber-700">
        <p className="text-sm text-slate-500 dark:text-slate-400">Incomplete queue row</p>
      </Card>
    );
  }
  const prayerCategories = getPrayerCategories(prayerRequest ?? {});
  const categoryLabels = prayerCategories.length > 0 ? prayerCategories : ['General'];
  const displayName = prayerRequest?.isAnonymous
    ? 'Anonymous'
    : (formatFullName(prayerRequest?.firstName ?? null, prayerRequest?.lastName ?? null) || 'Anonymous');

  return (
    <Card
      padding="md"
      className="transition-all duration-200 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-md"
    >
      <div className="flex flex-col h-full">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {typeLabel ? (
            <Badge variant="info" className="capitalize">
              {typeLabel}
            </Badge>
          ) : null}
          {categoryLabels.map((cat) => (
            <Badge key={cat} variant={getCategoryBadgeVariant(cat)} className="capitalize">
              {cat}
            </Badge>
          ))}
          <span className="text-slate-600 dark:text-slate-400">{displayName}</span>
        </div>

        <p className="mb-3 text-base text-slate-800 dark:text-slate-200 line-clamp-2">
          {prayerRequest?.body ?? ''}
        </p>

        <p className="text-sm text-slate-500 dark:text-slate-400">
          Displayed {displayCount} time{displayCount === 1 ? '' : 's'}
        </p>

        <CardFooter className="mt-4 justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDisplay(prayerRequestId)}
              className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20"
              leftIcon={
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              }
            >
              Display Live
            </Button>
            {onEdit && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(prayerRequest)}
                aria-label="Edit prayer"
                leftIcon={
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                }
              >
                Edit
              </Button>
            )}
          </div>
          {onRemove && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRemove(prayerRequestId)}
              aria-label="Remove from queue"
              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
              leftIcon={
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              }
            >
              Remove
            </Button>
          )}
        </CardFooter>
      </div>
    </Card>
  );
}
